<script lang="ts">
    import { theme } from '$theme/theme.svelte';
    import Text from '$components/common/text/text.svelte';
    import Button from '$components/common/button/button.svelte';
    import CheckmarkCircle from '$assets/icons/checkmark-circle.svelte';
    import ExclamationMarkCircle from '$assets/icons/exclamation-mark-circle.svelte';
    import XClose from '$assets/icons/x-close.svelte';
    import CloseCircle from '$assets/icons/close-circle.svelte';
    import type { ComponentType } from 'svelte';
    import { onMount } from 'svelte';

    export let size: 'medium' | 'small' = 'medium';
    export let placeholder: string | '' = '';
    export let value = '';
    export let label = '';
    export let name: string | undefined = undefined;
    export let disabled = false;
    export let required = false;
    export let outline = true;
    export let clear = false;
    export let search = false;
    export let focused = false;
    export let id: string | undefined = undefined;
    export let darkMode = false;

    export let helperText = '';
    export let success = false;
    export let successText = '';

    export let error = false;
    export let errorText = '';

    export let warning = false;
    export let warningText = '';

    export let iconStart: ComponentType | undefined = undefined;
    export let iconStartProps: {
        width?: string;
        height?: string;
        fill?: string;
        color?: string;
        stroke?: string;
    } = {};

    export let iconEnd: ComponentType | undefined = undefined;
    export let iconEndProps: {
        width?: string;
        height?: string;
        fill?: string;
        color?: string;
        stroke?: string;
    } = {};

    let inputElement: HTMLInputElement;

    onMount(() => {
        if (focused) {
            inputElement.focus();
        }
    });
</script>

<div class="text-input-container">
    {#if label}
        <label for={id}>
            <Text
                color={darkMode ? $theme.colors.white : $theme.colors.primary[900]}
                variant="bodyLarge"
                bold
                style="margin-bottom: {$theme.spacing[2]}"
            >
                {label}
                {#if required}<span style:color={$theme.colors.primary[500]}>*</span>{/if}
            </Text>
        </label>
    {/if}
    <div class="input-wrapper">
        {#if iconStart}
            <div class="icon-start">
                <svelte:component this={iconStart} {...iconStartProps} />
            </div>
        {/if}
        <input
            bind:this={inputElement}
            class:outline
            {...$$restProps}
            bind:value
            {disabled}
            {placeholder}
            class:light-mode={!darkMode}
            class:dark-mode={darkMode}
            class:has-text={value.length &&
                !success &&
                !error &&
                !warning &&
                !disabled &&
                !darkMode}
            class:no-text={!value.length &&
                !success &&
                !error &&
                !warning &&
                !disabled &&
                !darkMode}
            class:has-text-dark-mode={value.length &&
                !success &&
                !error &&
                !warning &&
                !disabled &&
                darkMode}
            class:no-text-dark-mode={!value.length &&
                !success &&
                !error &&
                !warning &&
                !disabled &&
                darkMode}
            style:background-color={!success &&
            !error &&
            !warning &&
            !disabled &&
            !darkMode &&
            value.length > 0
                ? $theme.colors.white
                : ''}
            class:success={success && !error && !warning && !disabled && !darkMode}
            class:success-dark-mode={success && !error && !warning && !disabled && darkMode}
            class:error={error && !success && !warning && !disabled && !darkMode}
            class:error-dark-mode={error && !success && !warning && !disabled && darkMode}
            class:warning={warning && !success && !error && !disabled && !darkMode}
            class:warning-dark-mode={warning && !success && !error && !disabled && darkMode}
            class:medium={size === 'medium'}
            class:small={size === 'small'}
            style:padding-left={iconStart ? $theme.spacing[22] : $theme.spacing[8]}
            style:padding-right={iconEnd ? $theme.spacing[22] : $theme.spacing[8]}
            on:focus
            on:blur
            on:input
            on:change
            {id}
            name={name || id}
        />
        {#if iconEnd || search || clear}
            <div class="icon-end">
                {#if search && value.length}
                    <Button
                        size="xxSmall"
                        icon={XClose}
                        iconProps={{
                            width: '13',
                            height: '13',
                        }}
                        on:click={() => {
                            value = '';
                            success = false;
                            error = false;
                            warning = false;
                        }}
                        class="search"
                    />
                {:else if clear && value.length}
                    <Button
                        icon={CloseCircle}
                        on:click={() => {
                            value = '';
                        }}
                        size="none"
                        variant="text"
                        class="clear"
                    />
                {:else}
                    <svelte:component this={iconEnd} {...iconEndProps} />
                {/if}
            </div>
        {/if}
    </div>
    {#if helperText && !success && !error && !warning}
        <Text
            variant="bodySmall"
            color={darkMode ? $theme.colors.common.dark.soft : $theme.colors.common.light.medium}
            class="guiding-text"
        >
            {helperText}
        </Text>
    {/if}
    {#if success && successText && !error && !warning && !disabled}
        <Text
            variant="bodySmall"
            color={darkMode
                ? $theme.colors.semantic.success[400]
                : $theme.colors.semantic.success[500]}
            class="guiding-text"
        >
            <CheckmarkCircle
                width={16}
                height={17}
                fill={darkMode
                    ? $theme.colors.semantic.success[400]
                    : $theme.colors.semantic.success[500]}
            />
            {successText}
        </Text>
    {/if}
    {#if error && errorText && !success && !warning && !disabled}
        <Text
            variant="bodySmall"
            color={darkMode ? $theme.colors.semantic.error[400] : $theme.colors.semantic.error[500]}
            class="guiding-text"
        >
            <ExclamationMarkCircle
                width={16}
                height={17}
                fill={darkMode
                    ? $theme.colors.semantic.error[400]
                    : $theme.colors.semantic.error[500]}
            />
            {errorText}
        </Text>
    {/if}
    {#if warning && warningText && !success && !error && !disabled}
        <Text
            variant="bodySmall"
            color={darkMode
                ? $theme.colors.semantic.warning[400]
                : $theme.colors.semantic.warning[500]}
            class="guiding-text"
        >
            <ExclamationMarkCircle
                fill={darkMode
                    ? $theme.colors.semantic.warning[400]
                    : $theme.colors.semantic.warning[500]}
                width={16}
                height={17}
            />
            {warningText}
        </Text>
    {/if}
</div>

<style>
    .text-input-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        width: 100%;
    }
    input {
        width: 100%;
        /* bodyLarge */
        font-family: 'Inter', serif;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0.5px;
        caret-color: var(--colors-primary-500);
        border: none;
    }

    .outline {
        border: 1px solid;
    }

    .light-mode {
        color: var(--colors-primary-900);
    }

    .dark-mode {
        color: var(--colors-white);
        border-color: #ffffff4d;
        background-color: #ffffff4d;
    }

    .no-text {
        border-color: var(--colors-line-medium);
    }

    .has-text {
        border-color: var(--colors-line-strong);
    }

    .light-mode:focus {
        outline: none;
        border-color: var(--colors-primary-500);
        background-color: var(--colors-primary-50);
    }

    .dark-mode:focus {
        outline: none;
        border-color: var(--colors-primary-500);
        background-color: #4bb69c33;
    }

    .light-mode::placeholder {
        color: var(--colors-common-light-soft);
    }
    .dark-mode::placeholder {
        color: var(--colors-common-dark-medium);
    }

    .light-mode:disabled {
        background-color: var(--colors-neutral-50);
        border-color: var(--colors-line-medium);
        color: var(--colors-neutral-300);
        cursor: not-allowed;
    }

    .light-mode:disabled::placeholder {
        color: var(--colors-neutral-300);
    }

    .dark-mode:disabled {
        background-color: #f0f2f50d;
        border-color: #d7dbd833;
        color: #82979280;
        cursor: not-allowed;
    }

    .dark-mode:disabled::placeholder {
        color: #82979280;
    }

    .text-input-container :global(.guiding-text) {
        margin-top: var(--spacing-02);
        display: flex;
        align-items: center;
        gap: 0px var(--spacing-02);
    }

    .success {
        background-color: var(--colors-semantic-success-100);
        border-color: var(--colors-semantic-success-500);
    }

    .success-dark-mode {
        background-color: #7bc12a33;
        border-color: var(--colors-semantic-success-500);
    }

    .error {
        background-color: var(--colors-semantic-error-100);
        border-color: var(--colors-semantic-error-500);
    }

    .error-dark-mode {
        background-color: #ff424533;
        border-color: var(--colors-semantic-error-500);
    }

    .warning {
        background-color: var(--colors-semantic-warning-100);
        border-color: var(--colors-semantic-warning-500);
    }

    .warning-dark-mode {
        background-color: #f9b0271a;
        border-color: var(--colors-semantic-warning-500);
    }

    .medium {
        height: var(--spacing-24);
        border-radius: var(--spacing-04);
    }

    .small {
        height: var(--spacing-20);
        border-radius: var(--spacing-04);
    }

    .input-wrapper {
        position: relative;
        width: 100%;
    }
    .input-wrapper :global(.icon-start) {
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: var(--spacing-26);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .input-wrapper :global(.icon-end) {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: var(--spacing-26);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .icon-end :global(.search) {
        background-color: var(--colors-neutral-600);
        border: 1px solid var(--colors-neutral-600);
    }
</style>
